(function () {
  const SELECTORS = {
    root: "[data-ctr-timeline]",
    slider: "[data-ctr-timeline-slider]",
    bar: "[data-ctr-timeline-bar]",
    handle: "[data-ctr-timeline-handle]",
    progress: "[data-ctr-timeline-progress]",
    dot: ".ctr-timeline__dot",
    label: ".ctr-timeline__label",
    panel: "[data-ctr-timeline-stage]",
    video: "[data-ctr-timeline-video]"
  };

  const DOT_START = { r: 134, g: 206, b: 250 };
  const DOT_END = { r: 29, g: 82, b: 116 };
  const LABEL_START = { r: 175, g: 225, b: 255 };
  const LABEL_END = { r: 29, g: 82, b: 116 };

  const instances = new Map();

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function lerp(a, b, t) {
    return Math.round(a + (b - a) * t);
  }

  function colorAt(start, end, t) {
    return "rgb(" + lerp(start.r, end.r, t) + ", " + lerp(start.g, end.g, t) + ", " + lerp(start.b, end.b, t) + ")";
  }

  function pauseOtherVideos(currentVideo) {
    const allVideos = document.querySelectorAll(SELECTORS.video);
    allVideos.forEach(function (video) {
      if (video !== currentVideo) {
        video.pause();
      }
    });
  }

  function Timeline(root) {
    this.root = root;
    this.slider = root.querySelector(SELECTORS.slider);
    this.bar = root.querySelector(SELECTORS.bar);
    this.handle = root.querySelector(SELECTORS.handle);
    this.progress = root.querySelector(SELECTORS.progress);
    this.dots = Array.from(root.querySelectorAll(SELECTORS.dot));
    this.labels = Array.from(root.querySelectorAll(SELECTORS.label));
    this.panels = Array.from(root.querySelectorAll(SELECTORS.panel));
    this.videos = Array.from(root.querySelectorAll(SELECTORS.video));
    this.activeIndex = 0;
    this.isDragging = false;
    this.pointerId = null;
    this.handlers = {};
  }

  Timeline.prototype.init = function () {
    if (!this.slider || !this.bar || !this.handle || !this.labels.length || !this.panels.length) {
      return;
    }

    this.applyStageColors();
    this.bindEvents();
    this.render(0, false);
  };

  Timeline.prototype.applyStageColors = function () {
    const count = this.labels.length;

    this.labels.forEach(function (label, index) {
      const t = count > 1 ? index / (count - 1) : 0;
      label.style.setProperty("--ctr-label-color", colorAt(LABEL_START, LABEL_END, t));
    });

    this.dots.forEach(function (dot, index) {
      const t = count > 1 ? index / (count - 1) : 0;
      dot.style.setProperty("--ctr-dot-color", colorAt(DOT_START, DOT_END, t));
    });

    if (this.handle) {
      this.handle.style.setProperty("--ctr-handle-color", colorAt(DOT_START, DOT_END, 0));
    }

    this.colorForPercent = function (percent) {
      const t = clamp(percent / 100, 0, 1);
      return colorAt(DOT_START, DOT_END, t);
    };
  };

  Timeline.prototype.bindEvents = function () {
    const self = this;

    this.handlers.labelClick = function (event) {
      const target = event.currentTarget;
      const index = Number(target.getAttribute("data-stage-index"));
      if (Number.isFinite(index)) {
        self.render(index, true);
      }
    };

    this.labels.forEach(function (label) {
      label.addEventListener("click", self.handlers.labelClick);
    });

    this.handlers.barPointerDown = function (event) {
      if (event.button !== undefined && event.button !== 0) {
        return;
      }
      self.startDrag(event);
    };

    this.handlers.pointerMove = function (event) {
      if (!self.isDragging || event.pointerId !== self.pointerId) {
        return;
      }
      event.preventDefault();
      self.dragTo(event.clientX);
    };

    this.handlers.pointerEnd = function (event) {
      if (!self.isDragging || event.pointerId !== self.pointerId) {
        return;
      }
      self.endDrag();
    };

    this.bar.addEventListener("pointerdown", this.handlers.barPointerDown);
    window.addEventListener("pointermove", this.handlers.pointerMove);
    window.addEventListener("pointerup", this.handlers.pointerEnd);
    window.addEventListener("pointercancel", this.handlers.pointerEnd);

    this.handlers.handleKeydown = function (event) {
      const max = self.labels.length - 1;
      let next = self.activeIndex;
      switch (event.key) {
        case "ArrowRight":
        case "ArrowUp":
          next = clamp(self.activeIndex + 1, 0, max);
          break;
        case "ArrowLeft":
        case "ArrowDown":
          next = clamp(self.activeIndex - 1, 0, max);
          break;
        case "Home":
          next = 0;
          break;
        case "End":
          next = max;
          break;
        default:
          return;
      }
      event.preventDefault();
      self.render(next, false);
    };

    this.handle.addEventListener("keydown", this.handlers.handleKeydown);

    this.handlers.videoPlay = function (event) {
      pauseOtherVideos(event.target);
    };

    this.videos.forEach(function (video) {
      video.addEventListener("play", self.handlers.videoPlay);
    });
  };

  Timeline.prototype.startDrag = function (event) {
    this.isDragging = true;
    this.pointerId = event.pointerId;
    this.bar.classList.add("is-dragging");
    try {
      this.bar.setPointerCapture(event.pointerId);
    } catch (_) {
      // ignore - some browsers/elements may throw
    }
    this.dragTo(event.clientX);
  };

  Timeline.prototype.dragTo = function (clientX) {
    const rect = this.bar.getBoundingClientRect();
    if (!rect.width) {
      return;
    }
    const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
    const percent = ratio * 100;
    const max = this.labels.length - 1;
    const floatIndex = ratio * max;
    const nearestIndex = clamp(Math.round(floatIndex), 0, max);

    this.updateProgress(percent);
    this.updateHandle(percent);

    if (nearestIndex !== this.activeIndex) {
      this.setActive(nearestIndex, false);
    }
  };

  Timeline.prototype.endDrag = function () {
    if (!this.isDragging) {
      return;
    }
    this.isDragging = false;
    this.bar.classList.remove("is-dragging");
    try {
      if (this.pointerId !== null) {
        this.bar.releasePointerCapture(this.pointerId);
      }
    } catch (_) {
      // ignore
    }
    this.pointerId = null;
    this.render(this.activeIndex, false);
  };

  Timeline.prototype.updateProgress = function (percent) {
    this.root.style.setProperty("--ctr-timeline-progress", percent + "%");
    if (this.handle && this.colorForPercent) {
      this.handle.style.setProperty("--ctr-handle-color", this.colorForPercent(percent));
    }
  };

  Timeline.prototype.updateHandle = function (percent) {
    if (this.handle) {
      this.handle.style.setProperty("--ctr-handle-pos", percent + "%");
    }
  };

  Timeline.prototype.setActive = function (index, focusActiveTab) {
    this.activeIndex = index;

    this.labels.forEach(function (label, labelIndex) {
      const isActive = labelIndex === index;
      label.classList.toggle("is-active", isActive);
      label.setAttribute("aria-selected", isActive ? "true" : "false");
      label.tabIndex = isActive ? 0 : -1;
    });

    this.panels.forEach(function (panel, panelIndex) {
      const isActive = panelIndex === index;
      panel.classList.toggle("is-active", isActive);
      panel.hidden = !isActive;
    });

    if (this.handle) {
      this.handle.setAttribute("aria-valuenow", String(index));
    }

    if (focusActiveTab && this.labels[index]) {
      this.labels[index].focus();
    }
  };

  Timeline.prototype.render = function (index, focusActiveTab) {
    const max = this.labels.length - 1;
    this.activeIndex = clamp(index, 0, max);
    const percent = max > 0 ? (this.activeIndex / max) * 100 : 50;

    this.updateProgress(percent);
    this.updateHandle(percent);
    this.setActive(this.activeIndex, focusActiveTab);
  };

  Timeline.prototype.openByBlockId = function (blockId) {
    if (!blockId) {
      return;
    }
    const targetIndex = this.labels.findIndex(function (label) {
      return label.dataset.blockId === blockId;
    });
    if (targetIndex >= 0) {
      this.render(targetIndex, true);
    }
  };

  Timeline.prototype.destroy = function () {
    const self = this;

    this.labels.forEach(function (label) {
      label.removeEventListener("click", self.handlers.labelClick);
    });

    if (this.bar) {
      this.bar.removeEventListener("pointerdown", this.handlers.barPointerDown);
    }
    window.removeEventListener("pointermove", this.handlers.pointerMove);
    window.removeEventListener("pointerup", this.handlers.pointerEnd);
    window.removeEventListener("pointercancel", this.handlers.pointerEnd);

    if (this.handle) {
      this.handle.removeEventListener("keydown", this.handlers.handleKeydown);
    }

    this.videos.forEach(function (video) {
      video.removeEventListener("play", self.handlers.videoPlay);
      video.pause();
    });
  };

  function initSection(root) {
    if (!root || instances.has(root)) {
      return;
    }
    const timeline = new Timeline(root);
    timeline.init();
    instances.set(root, timeline);
  }

  function destroySection(root) {
    if (!root || !instances.has(root)) {
      return;
    }
    const instance = instances.get(root);
    instance.destroy();
    instances.delete(root);
  }

  function initAll() {
    const roots = document.querySelectorAll(SELECTORS.root);
    roots.forEach(initSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", function (event) {
    const root = event.target.querySelector(SELECTORS.root);
    initSection(root);
  });

  document.addEventListener("shopify:section:unload", function (event) {
    const root = event.target.querySelector(SELECTORS.root);
    destroySection(root);
  });

  document.addEventListener("shopify:section:reorder", initAll);

  document.addEventListener("shopify:block:select", function (event) {
    const sectionRoot = event.target.closest(SELECTORS.root) || event.target.querySelector(SELECTORS.root);
    if (!sectionRoot || !instances.has(sectionRoot)) {
      return;
    }
    instances.get(sectionRoot).openByBlockId(event.detail.blockId);
  });
})();
